import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = "dvnlc_chat_session";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export default function AIChatWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: t("chat.greeting") }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Reset greeting when language changes (only if only greeting is present)
  useEffect(() => {
    setMessages((m) => (m.length === 1 && m[0].role === "assistant" ? [{ role: "assistant", content: t("chat.greeting") }] : m));
  }, [i18n.language, t]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: getSessionId(),
        message: text,
        lang: (i18n.language || "en").split("-")[0],
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: t("chat.error") }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : t("chat.trigger")}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 shadow-2xl shadow-amber-500/30 hover:from-amber-300 hover:to-amber-500 transition-all px-4 py-3 font-semibold text-sm border border-amber-300/40"
      >
        {open ? (
          <X className="w-5 h-5" />
        ) : (
          <>
            <MessageCircle className="w-5 h-5" />
            <span className="hidden sm:inline">{t("chat.trigger")}</span>
          </>
        )}
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed z-50 bottom-24 right-4 sm:right-5 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col bg-[#0A1128] border border-amber-500/30 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden"
        >
          <div className="flex items-center gap-3 px-5 py-4 border-b border-amber-500/20 bg-[#0F1C3F]">
            <div className="w-9 h-9 rounded-md border border-amber-500/40 flex items-center justify-center bg-slate-950/60">
              <Shield className="w-4 h-4 text-amber-400" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-sm text-slate-50">{t("chat.title")}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400/80 font-mono">
                {t("chat.subtitle")}
              </div>
            </div>
          </div>

          <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0A1128]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-amber-500/90 text-slate-950" : "bg-[#0F1C3F] border border-amber-500/20 text-slate-100"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[#0F1C3F] border border-amber-500/20 text-slate-400 rounded-lg px-3 py-2 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex items-end gap-2 px-3 py-3 border-t border-amber-500/20 bg-[#0F1C3F]">
            <textarea
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={t("chat.placeholder")}
              className="flex-1 resize-none bg-slate-950/70 border border-amber-500/25 rounded-md text-slate-100 text-sm px-3 py-2 focus:outline-none focus:border-amber-400/60 max-h-32"
              disabled={sending}
            />
            <button type="submit" data-testid="ai-chat-send" disabled={sending || !input.trim()} className="h-10 w-10 flex items-center justify-center rounded-md bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Send message">
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="px-4 pb-2 text-[10px] text-slate-500 font-mono uppercase tracking-widest bg-[#0F1C3F]">
            {t("chat.footer")}
          </div>
        </div>
      )}
    </>
  );
}
